/**
 Blurhash implementation for images:
 https://blurha.sh/
 https://github.com/woltapp/blurhash/tree/master/TypeScript
*/

import { decode, encode } from 'blurhash';

import { createImage, getImageData, imageToBase64 } from './image-file';

/**
 * How do I pick the number of X and Y components?
 * It depends a bit on taste. The more components you pick, the more information is retained in the placeholder,
 * but the longer the BlurHash string will be. Also, it doesn't always look good with too many components.
 * However, you should adjust the number of components depending on the aspect ratio of your images.
 * For instance, very wide images should have more X components and fewer Y components.
 */
const componentX = 4;
const componentY = 4;

export const imageToBlurhash = async (image: Blob | File, resize: number = undefined): Promise<string> => {
  const base64 = await imageToBase64(image);
  const imageFile = await createImage(base64);
  const imageData = getImageData(imageFile, resize);

  return encode(imageData.data, imageData.width, imageData.height, componentX, componentY);
};

export const imageUrlToBlurhash = async (imageUrl: string, resize: number = undefined): Promise<string> => {
  const imageFile = await createImage(imageUrl);
  const imageData = getImageData(imageFile, resize);

  return encode(imageData.data, imageData.width, imageData.height, componentX, componentY);
};

export const blurhashToImage = (hash: string, width: number, height: number): string => {
  const pixels = decode(hash, width, height);
  const canvas = document.createElement('canvas');

  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  const imageData = ctx.createImageData(width, height);

  imageData.data.set(pixels);
  ctx.putImageData(imageData, 0, 0);
  canvas.remove();

  return canvas.toDataURL();
};

// eslint-disable-next-line max-len
export const fallbackBlurImage =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAADRElEQVRYR51XCXLbMAwk5f9/r3Xi+8ovKnaIcwFScdPMZHRY9i4Wi0P163lvxf5aKfFKPpGb8VDCw/4jPzqr/0wggAPLH8GND9evx236a3wzK5LliV/tVxUxariaUp0SaAFYqNDBAeMz8NuGySd+OSeTCLTSDIOjJyDCtrOJMk7AgxZAOtRNIvXrfkVIAzNoYdToKCSc5dwBwoJghUCpSgKJlUIEnIGedSVUegEWAlEVTYsCueiVSPB9JsLXzM3TUV+3S3JSj7JrD0eNPpGaeJqAGEMBGXyLRH1dlcAkYiHR2sqEEpHYByBiA18YfJNMKfV1PbO/1eUh8pVB6R6e873hLwAtBFyrklgkBaBIv/O8dALgdIiSI2fgfs5kVksROMAk14gZ2EkYkcKE1Bv1eT5Zj0OJXfYOvJa2dmBIhSol1a7mwojpfOkk/B9TQqZ8no5qLal7BQFgip7/gxegManJPHIBBQKsCKsi5VDq43SArisSm+QSuaVCiagHuC+Y60FyBtuVigrIOZejVMnjyARG+VGBP6ZAVoGNiKW2ACgQWHZiSFeAivN++IwewOg1980JuCm5QQUC5nqVn0GrgPP5UioasRPwwQJu76YD4LYqCX6GDSMEhvIDUAVPClgKbp8f/6WAdUqrAkkDON4iX3ZckmhIbdNXIqCtINU7qZCUCJWAU9AJmNtT9FqOoTteP/bSA0VS6HpU+0MFSClS/t0DQxkG94sXUlckES5EABXwHCuB0BGlGYX9ACZebkRairkH8MiqncDvcRZYu/U2rO73VowbkoxjmnpaZt6IInivAmhE5z0TUDm9H2BH7I/4MKLFBIeRbj3DFGQSnPM8lGRT6gSsFdrqJROQPpmNYsy/GjHNfQHlaGPz0V2BPjqRAj5aVQFcSDxi3BlSCmgH0Z1g3AGw/eJ6Vk/7X7oJSCjRkGFUo/NxHcBNWA2p7TmvZrgndtJHIeAVDQTUG4EibsfQBywTykZT4rNC2zbuhRMCLK0FGDafyRaU1yJYOsNWbM/hAltKPQwK6JNbYNskbPuPr0eRYnpb+obAt98b1sG3N9Ibk2VsUwGIYuOlKr0IvqUwfZueKxDAE/z79803TELXKX8BfmcFfQOYL3IAAAAASUVORK5CYII=';
